import { lazy, Suspense } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";

import { AppShell } from "@/app/AppShell";
import { AdminAppShell } from "@/app/AdminAppShell";
import { LangMenu } from "@/components/widgets/LangMenu";
import { AuthGuard } from "@/lib/auth-guard";
import { currentTypeCode } from "@/lib/nav/navPath";
import { AdminGuard } from "@/lib/admin-guard";
import { MENU_DASHBOARD_OVERALL, MENU_DASHBOARD_PERSONAL } from "@/lib/dynamic/menuPermissions";

import {
  DashboardIndexRedirect,
  DashboardMenuGuard,
  HomeRedirect,
  TypeMenuGuard,
} from "./menuGuards";

/**
 * 시스템 어드민(공급사) 콘솔 진입 판정 — prod 서브도메인 감지.
 * admin.eqms.co.kr 처럼 호스트가 "admin." 으로 시작하면 앱 루트가 어드민 콘솔이 됨.
 * localhost 등 개발환경은 서브도메인이 불가하므로 /admin/* 경로 폴백을 별도로 제공함.
 */
const isAdminHost = (): boolean =>
  typeof window !== "undefined" && window.location.hostname.startsWith("admin.");

/* ── lazy 코드 분할 (vite-conventions §빌드·린트: "라우트별 lazy() 코드 분할 필수") ── */
const AuthCallbackPage = lazy(() =>
  import("@/pages/AuthCallbackPage").then((m) => ({ default: m.AuthCallbackPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);
const PersonalDashboardPage = lazy(() =>
  import("@/pages/PersonalDashboardPage").then((m) => ({ default: m.PersonalDashboardPage })),
);
const OverallDashboardPage = lazy(() =>
  import("@/pages/OverallDashboardPage").then((m) => ({ default: m.OverallDashboardPage })),
);
const InviteAcceptPage = lazy(() =>
  import("@/pages/InviteAcceptPage").then((m) => ({ default: m.InviteAcceptPage })),
);
const SignupPage = lazy(() =>
  import("@/pages/SignupPage").then((m) => ({ default: m.SignupPage })),
);
const SignupVerifyPage = lazy(() =>
  import("@/pages/SignupVerifyPage").then((m) => ({ default: m.SignupVerifyPage })),
);
const ItemDetailPage = lazy(() =>
  import("@/pages/ItemDetailPage").then((m) => ({ default: m.ItemDetailPage })),
);
const LoginPage = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const NoticePage = lazy(() =>
  import("@/pages/NoticePage").then((m) => ({ default: m.NoticePage })),
);
const ProductPage = lazy(() =>
  import("@/pages/ProductPage").then((m) => ({ default: m.ProductPage })),
);
const ProjectListPage = lazy(() =>
  import("@/pages/ProjectListPage").then((m) => ({ default: m.ProjectListPage })),
);
const SupplierPage = lazy(() =>
  import("@/pages/SupplierPage").then((m) => ({ default: m.SupplierPage })),
);
const TrainingPage = lazy(() =>
  import("@/pages/TrainingPage").then((m) => ({ default: m.TrainingPage })),
);
const TrainingCertLedgerPage = lazy(() =>
  import("@/pages/TrainingCertLedgerPage").then((m) => ({ default: m.TrainingCertLedgerPage })),
);
const TrainingHistoryPage = lazy(() =>
  import("@/pages/TrainingHistoryPage").then((m) => ({ default: m.TrainingHistoryPage })),
);
const TraceabilityPage = lazy(() =>
  import("@/pages/TraceabilityPage").then((m) => ({ default: m.TraceabilityPage })),
);
const WorkitemTypePage = lazy(() =>
  import("@/pages/WorkitemTypePage").then((m) => ({ default: m.WorkitemTypePage })),
);
const WorkspaceResolverPage = lazy(() =>
  import("@/pages/WorkspaceResolverPage").then((m) => ({ default: m.WorkspaceResolverPage })),
);

// 설정 페이지
const AccountSettingsPage = lazy(() =>
  import("@/pages/settings/AccountSettingsPage").then((m) => ({ default: m.AccountSettingsPage })),
);
const CodeManagerPage = lazy(() =>
  import("@/pages/settings/CodeManagerPage").then((m) => ({ default: m.CodeManagerPage })),
);
const FieldBuilderPage = lazy(() =>
  import("@/pages/settings/FieldBuilderPage").then((m) => ({ default: m.FieldBuilderPage })),
);
const PersonnelPage = lazy(() =>
  import("@/pages/settings/PersonnelPage").then((m) => ({ default: m.PersonnelPage })),
);
const ProvisioningPage = lazy(() =>
  import("@/pages/settings/ProvisioningPage").then((m) => ({ default: m.ProvisioningPage })),
);
const RolesPage = lazy(() =>
  import("@/pages/settings/RolesPage").then((m) => ({ default: m.RolesPage })),
);
const SystemSettingsPage = lazy(() =>
  import("@/pages/settings/SystemSettingsPage").then((m) => ({ default: m.SystemSettingsPage })),
);
const WorkspaceSettingsPage = lazy(() =>
  import("@/pages/settings/WorkspaceSettingsPage").then((m) => ({
    default: m.WorkspaceSettingsPage,
  })),
);

// 시스템 어드민(공급사) 콘솔 페이지 — 별도 셸(AdminAppShell) 하위
const AdminCompaniesPage = lazy(() =>
  import("@/pages/admin/CompaniesPage").then((m) => ({ default: m.CompaniesPage })),
);
const AdminGlobalCodesPage = lazy(() =>
  import("@/pages/admin/GlobalCodesPage").then((m) => ({ default: m.GlobalCodesPage })),
);
const AdminSecurityPolicyPage = lazy(() =>
  import("@/pages/admin/SecurityPolicyPage").then((m) => ({ default: m.SecurityPolicyPage })),
);
const AdminAuditLogPage = lazy(() =>
  import("@/pages/admin/AuditLogPage").then((m) => ({ default: m.AuditLogPage })),
);
const AdminWorkflowBuilderPage = lazy(() =>
  import("@/pages/admin/AdminWorkflowBuilderPage").then((m) => ({
    default: m.AdminWorkflowBuilderPage,
  })),
);

/** m/:typeCode → w/${slug} 영구 리다이렉트 (북마크·외부 링크 하위 호환). */
function LegacyTypeCodeRedirect() {
  const { typeCode } = useParams<{ typeCode: string }>();
  const code = currentTypeCode(typeCode) ?? typeCode ?? "";
  return <Navigate to={`../w/${code.toLowerCase()}`} replace />;
}

function LegacyTypeCodeItemRedirect() {
  const { typeCode, itemId } = useParams<{ typeCode: string; itemId: string }>();
  const code = currentTypeCode(typeCode) ?? typeCode ?? "";
  return <Navigate to={`../w/${code.toLowerCase()}/${itemId ?? ""}`} replace />;
}

/** 이름이 바뀐 업무유형의 옛 주소(w/cmp 등) → 현재 주소. 쿼리(item·filter)는 유지. */
function RenamedTypeCodeRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`../${to.toLowerCase()}${search}`} relative="path" replace />;
}

/**
 * 업무유형 라우터 — 대부분 제네릭 WorkitemTypePage 로 렌더하되, 전용 뷰가 필요한 유형만 분기:
 *  - 교육 관리(TRN) → ISO 13485 6.2 전용 TrainingPage (Green eQMS 목록·등록·상세 드로어·단계 진행).
 *  - 자격인증관리대장(TRN_CERTIFY) → TrainingCertLedgerPage (결과보고 발급 인증서 대장·출력·배경).
 *  - 개인별 교육 이력(TRN_RECORD) → TrainingHistoryPage (본인 / 전사 조회 — 교육훈련 이력·자격인증현황).
 *  - 제품관리(PRODUCT) → ProductPage (Green eQMS 목록·대시보드·등록 마법사·상세 드로어).
 *  - 공지사항(NOTICE) → NoticePage (Green eQMS 목록·상세·등록 창, 열람 기록·전체 메일).
 *  - 공급자 관리(SUP) → SupplierPage (Green eQMS 통계 카드·승인 공급자 목록·하위 기록 탭).
 * (CreateWizard 의 typeCode 별 리치 분기와 동일 철학 — 데이터는 전부 메타/시드 구동. 엔진 미수정.)
 *
 * P1: w/:slug 라우트(slug=lower(typeCode))와 레거시 m/:typeCode 양쪽에서 호출.
 * typeCode 파라미터를 대문자로 정규화하여 케이스 무관하게 동작함.
 * 진입 가드 = 그 유형의 메뉴 조회 권한(TypeMenuGuard) — 이름이 바뀐 옛 코드는 새 주소로 보낸 뒤 판정.
 */
function WorkitemTypeRouter() {
  const { typeCode: rawTypeCode } = useParams<{ typeCode: string }>();
  const typeCode = rawTypeCode?.toUpperCase() ?? "";
  const renamed = currentTypeCode(typeCode);
  if (renamed) return <RenamedTypeCodeRedirect to={renamed} />;
  return (
    <TypeMenuGuard>
      <WorkitemTypeView typeCode={typeCode} />
    </TypeMenuGuard>
  );
}

/** 업무유형 코드(대문자)별 화면 선택 — {@link WorkitemTypeRouter} 참고. */
function WorkitemTypeView({ typeCode }: { typeCode: string }) {
  if (typeCode === "TRN") return <TrainingPage />;
  if (typeCode === "TRN_CERTIFY") return <TrainingCertLedgerPage />;
  if (typeCode === "TRN_RECORD") return <TrainingHistoryPage />;
  if (typeCode === "PRODUCT") return <ProductPage />;
  if (typeCode === "NOTICE") return <NoticePage />;
  if (typeCode === "SUP") return <SupplierPage />;
  return <WorkitemTypePage />;
}

/**
 * 시스템 어드민(공급사) 콘솔 라우트 서브트리 — 별도 셸(AdminAppShell) + 슈퍼관리자 가드.
 *
 * 두 진입점에서 재사용한다(중복 정의 방지):
 *   (a) prod: admin.eqms.co.kr 서브도메인 → 앱 루트("/")에 마운트.
 *   (b) dev/폴백: "/admin/*" 경로에 마운트(localhost 서브도메인 불가 대응).
 *
 * @param basePath 이 서브트리가 마운트되는 부모 경로("" = 루트, "admin" = /admin).
 *                 index/404 리다이렉트 대상 경로를 basePath 기준으로 구성함.
 */
function adminRouteTree(basePath: "" | "admin") {
  const provisioningPath = basePath ? `/${basePath}/provisioning` : "/provisioning";
  return (
    <Route
      element={
        <AdminGuard>
          <AdminAppShell />
        </AdminGuard>
      }
    >
      <Route index element={<Navigate to="provisioning" replace />} />
      <Route path="provisioning" element={<ProvisioningPage />} />
      <Route path="companies" element={<AdminCompaniesPage />} />
      <Route path="codes" element={<AdminGlobalCodesPage />} />
      <Route path="workflows" element={<AdminWorkflowBuilderPage />} />
      <Route path="security-policy" element={<AdminSecurityPolicyPage />} />
      <Route path="audit-log" element={<AdminAuditLogPage />} />
      {/* 어드민 콘솔 내부 404 → 모듈 프로비저닝(메인)으로 */}
      <Route path="*" element={<Navigate to={provisioningPath} replace />} />
    </Route>
  );
}

/**
 * 앱 라우팅 구성 (동적 메타모델)
 *
 *   /login                                  - 로그인 (Keycloak redirect)
 *   /auth/callback                          - OIDC 콜백
 *   /workspaces                             - 워크스페이스 선택
 *   /:workspace/dashboard                   - 품질 대시보드
 *   /:workspace/traceability                - 추적성 (폐쇄루프·설계 추적성 매트릭스)
 *   /:workspace/projects, /projects/:id     - 프로젝트 목록 / 드로어
 *   /:workspace/m/:typeCode[/:itemId]       - 업무유형별 아이템 목록/상세 (동적, 모듈 하드코딩 없음)
 *   /:workspace/settings/{workspace,field-builder,codes,users,roles} - 어드민
 *   /admin/{provisioning,companies,standards,platform} - 시스템 어드민 콘솔(공급사, 별도 셸)
 *   admin.eqms.co.kr 서브도메인 - 앱 루트가 시스템 어드민 콘솔
 */
/** 로그인 전 화면 공통 — 오른쪽 위 언어 선택(지구본 메뉴). */
function PublicLayout() {
  return (
    <>
      <LangMenu />
      <Outlet />
    </>
  );
}

export function AppRoutes() {
  // prod 서브도메인(admin.*) 진입 시 앱 루트 전체를 시스템 어드민 콘솔로 렌더.
  // 공개 경로(로그인/콜백)는 어드민 서브도메인에서도 필요하므로 함께 노출함.
  if (isAdminHost()) {
    return (
      <Suspense fallback={null}>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          {adminRouteTree("")}
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* 공개 경로 — 기존 /login(dev 폼 mock) 유지 + 계정 생애주기 추가(additive) */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* 로그인 전 화면 — 오른쪽 위 언어 선택(PublicLayout) */}
        <Route element={<PublicLayout />}>
          <Route path="/login" element={<LoginPage />} />
          {/* 계정 생애주기(D-02): 초대 수락 + 자가가입(초대 없음, 이메일 인증). 공개 라우트. */}
          <Route path="/invite/accept" element={<InviteAcceptPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/signup/verify" element={<SignupVerifyPage />} />
          {/* 비밀번호 찾기·재설정 — 공개 라우트(AuthGuard 밖). */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        {/* 워크스페이스 리졸버 (인증 필요) — 마지막 사용 회사·WS로 자동 직행(선택 화면 제거).
            루트·catch-all·에러복구·회사전환·인증콜백의 공통 착지 지점이므로 라우트는 유지하고 컴포넌트만 리졸버로 교체. */}
        <Route
          path="/workspaces"
          element={
            <AuthGuard>
              <WorkspaceResolverPage />
            </AuthGuard>
          }
        />

        {/* 워크스페이스 컨텍스트 라우트 (인증 + 워크스페이스 필요) */}
        <Route
          path="/:workspace"
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        >
          {/* 기본 진입 = 사이드바 첫 노출 메뉴(보통 개인 대시보드). 노출 메뉴가 없으면 권한 없음 안내. */}
          <Route index element={<HomeRedirect />} />

          {/* 개요 › 대시보드 — 개인(기본 진입) · 전체 2분리(목업 20260720 사이드바 구조).
              구 단일 경로 /dashboard 는 보이는 첫 대시보드로 리다이렉트(하위호환).
              각 경로는 대시보드 메뉴 권한(dashboard.personal/overall 조회)으로 진입 가드. */}
          <Route path="dashboard" element={<DashboardIndexRedirect />} />
          <Route
            path="dashboard/personal"
            element={
              <DashboardMenuGuard menuKey={MENU_DASHBOARD_PERSONAL}>
                <PersonalDashboardPage />
              </DashboardMenuGuard>
            }
          />
          <Route
            path="dashboard/overall"
            element={
              <DashboardMenuGuard menuKey={MENU_DASHBOARD_OVERALL}>
                <OverallDashboardPage />
              </DashboardMenuGuard>
            }
          />
          <Route path="traceability" element={<TraceabilityPage />} />

          {/* 역량·개발: 프로젝트 관리 */}
          {/* 프로젝트 템플릿은 설정 › 프로젝트 템플릿 탭으로 이전(Green eQMS). */}
          <Route path="projects" element={<ProjectListPage />} />
          <Route path="projects/:id" element={<ProjectListPage />} />

          {/* 신규 slug 기반 라우트 (w/:typeCode — typeCode 값은 소문자 slug) */}
          <Route path="w/:typeCode" element={<WorkitemTypeRouter />} />
          <Route
            path="w/:typeCode/:itemId"
            element={
              <TypeMenuGuard>
                <ItemDetailPage />
              </TypeMenuGuard>
            }
          />
          {/* 레거시 typeCode 라우트 → w/ 로 영구 리다이렉트 (북마크·외부 링크 하위 호환) */}
          <Route path="m/:typeCode" element={<LegacyTypeCodeRedirect />} />
          <Route path="m/:typeCode/:itemId" element={<LegacyTypeCodeItemRedirect />} />

          {/* 사용자 설정 (D-14: 언어·버전·프로필) — 모든 사용자 */}
          <Route path="settings/account" element={<AccountSettingsPage />} />

          {/* 설정 진입 = 시스템 설정 2탭 화면 (D-10: 사이트 관리자 · 인원 관리) */}
          <Route path="settings" element={<SystemSettingsPage />} />

          {/* 시스템 설정 (회사 관리자) */}
          <Route path="settings/workspace" element={<WorkspaceSettingsPage />} />
          {/* 필드 빌더: 2026-07-23 설정 메뉴에서 숨김(SystemSettingsPage TABS 주석 참조).
              라우트는 유지(딥링크/추후 재노출용) — field_defs가 커스텀 모듈 화면에 미반영이라
              메뉴에서만 감춤. 기획 확정 시 TABS 항목 주석 해제로 복구. */}
          <Route path="settings/field-builder" element={<FieldBuilderPage />} />
          <Route path="settings/codes" element={<CodeManagerPage />} />
          <Route path="settings/roles" element={<RolesPage />} />
          <Route path="settings/personnel" element={<PersonnelPage />} />

          {/* 모듈 프로비저닝(슈퍼관리자)은 회사 셸에서 제거 — 시스템 어드민 콘솔(AdminAppShell)로 이관.
              구 링크(/:workspace/provisioning) 진입은 어드민 콘솔로 리다이렉트. */}
          <Route path="provisioning" element={<Navigate to="/admin/provisioning" replace />} />
        </Route>

        {/* 시스템 어드민(공급사) 콘솔 — dev/폴백 경로(/admin/*). prod 는 admin.* 서브도메인 사용. */}
        <Route path="/admin">{adminRouteTree("admin")}</Route>

        {/* 루트 진입 -> 워크스페이스 선택으로 리다이렉트 */}
        <Route path="/" element={<Navigate to="/workspaces" replace />} />

        {/* 404 폴백 */}
        <Route path="*" element={<Navigate to="/workspaces" replace />} />
      </Routes>
    </Suspense>
  );
}
